import { visibilityTracker } from '../FreezeDetector/visibilityTracker';

export default class VideoOutputManager {
    #videoOutputs = new Map();
    #eventHandlers = new Map();
    #eventListeners = new Map();
    #observedElements = new Map(); // videoOutputId -> Element
    #logger;
    #cameraId;

    constructor(logger, cameraId) {
        this.#logger = logger;
        this.#cameraId = cameraId;
    }

    addVideoOutput(videoOutputId, transactionId) {
        if (this.#videoOutputs.get(videoOutputId)) return false;
        this.#videoOutputs.set(videoOutputId, transactionId);
        this.#logger(`addVideoOutput(): Added new video output: ${videoOutputId}`);
        return true;
    }

    removeVideoOutput(videoOutputId) {
        try {
            this.#logger(`Removing video output: ${videoOutputId}`);
            this.#unobserveElement(videoOutputId);
            this.setMediaStreamOnVideoOutput(videoOutputId, null);
            this.#removeEventListeners(videoOutputId);
            this.#videoOutputs.delete(videoOutputId);
        } catch (err) {
            this.#logger(`removeVideoOutput error: ${err.message}`, 'error');
            throw err;
        }
    }

    #observeElement(videoOutputId, element) {
        if (!element) return;
        const previous = this.#observedElements.get(videoOutputId);
        if (previous === element) return;
        if (previous) visibilityTracker.unobserve(previous);
        this.#observedElements.set(videoOutputId, element);
        visibilityTracker.observe(element, this.#cameraId);
    }

    #unobserveElement(videoOutputId) {
        const element = this.#observedElements.get(videoOutputId);
        if (!element) return;
        visibilityTracker.unobserve(element);
        this.#observedElements.delete(videoOutputId);
    }

    makeExclusiveForVideoOutputs(videoOutputIds) {
        const keep = new Set(videoOutputIds);
        for (let vOutputId of [...this.#videoOutputs.keys()]) {
            if (!keep.has(vOutputId)) this.removeVideoOutput(vOutputId);
        }
    }

    hasVideoOutputId(videoOutputId) {
        return this.getVideoOutputIds().includes(videoOutputId);
    }

    getVideoOutputIds() {
        return Array.from(this.#videoOutputs.keys());
    }

    getTransactionIds() {
        return [...new Set(Array.from(this.#videoOutputs.values()))];
    }

    isActiveOnVideoWall(videoWallTransactionId) {
        return this.getTransactionIds().includes(videoWallTransactionId);
    }

    isExclusivelyUsedForTransactionId(transactionId) {
        const transactions = this.getTransactionIds();
        if (transactions.length > 1) return false;
        return transactions[0] === transactionId;
    }

    setMediaStreamOnVideoOutputs(mediaStream) {
        const videoOutputIds = this.getVideoOutputIds();
        videoOutputIds.forEach((videoOutputId) => {
            this.setMediaStreamOnVideoOutput(videoOutputId, mediaStream);
        });
    }

    // setMediaStreamOnVideoOutput(videoOutputId, mediaStream) {
    //     const videoOutput = document.getElementById(videoOutputId);
    //     if (videoOutput) {
    //         videoOutput.srcObject = mediaStream;
    //         mediaStream && videoOutput.play();
    //         this.#logger(
    //             `#setMediaStreamOnVideoOutput(): ${mediaStream ? 'Attached' : 'Removed'} mediaStream ${
    //                 mediaStream ? 'to' : 'from'
    //             } videoOutputId=${videoOutputId}`
    //         );
    //     }
    // }

    async setMediaStreamOnVideoOutput(videoOutputId, mediaStream) {
        const MAX_RETRIES = 3;
        const RETRY_DELAY_MS = 50;

        const playVideo = async (id, stream, retryCount = 0) => {
            const el = document.getElementById(id);

            if (!el) {
                this.#logger(`Element ${id} not found in DOM.`);
                return;
            }
            if (stream) this.#observeElement(id, el);
            el.srcObject = stream;
            this.#logger(
                `#setMediaStreamOnVideoOutput(): ${stream ? 'Attached' : 'Removed'} mediaStream ${stream ? 'to' : 'from'} videoOutputId=${id}`
            );
            if (!stream) return;

            try {
                await el.play();
            } catch (err) {
                const currentEl = document.getElementById(id);
                const wasReplacedOrRemoved = !currentEl || currentEl !== el;

                if (wasReplacedOrRemoved && retryCount < MAX_RETRIES) {
                    this.#logger(`#setMediaStreamOnVideoOutput(): Element ${id} was replaced/removed. Retry ${retryCount + 1}/${MAX_RETRIES}...`);
                    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));

                    return playVideo(id, stream, retryCount + 1);
                }

                this.#logger(`#setMediaStreamOnVideoOutput(): Playback failed on ${id}: ${err.name}: ${err.message}`);
            }
        };

        await playVideo(videoOutputId, mediaStream);
    }

    addEventListener(type, handler, videoOutputId) {
        //ensure same handler reference is used across video elements (so removeEventListener can work as expected)
        const handlerPresent = this.#eventHandlers.get(type);
        if (!handlerPresent) {
            this.#eventHandlers.set(type, handler);
        }
        const eventHandler = handlerPresent || handler;

        const registeredListeners = this.#eventListeners.get(videoOutputId);

        if (registeredListeners && registeredListeners.includes(type)) return;

        registeredListeners && registeredListeners.push(type);

        const videoOutput = document.getElementById(videoOutputId);
        if (videoOutput) {
            videoOutput.addEventListener(type, eventHandler);
            this.#logger(`[VideoOutputManager videoOutputId=${videoOutputId} addEventListener(): For '${type}' registered: ${handler}`);
        }

        const types = registeredListeners || [type];
        this.#eventListeners.set(videoOutputId, types);
    }

    #removeEventListeners(videoOutputId) {
        const registeredListeners = this.#eventListeners.get(videoOutputId);
        if (!registeredListeners) return;

        registeredListeners.forEach((event) => {
            const handler = this.#eventHandlers.get(event);
            const videoOutput = document.getElementById(videoOutputId);
            if (videoOutput) {
                videoOutput.removeEventListener(event, handler);
                this.#logger(`[VideoOutputManager videoOutputId=${videoOutputId} removeEventListeners(): For '${event}' registered: ${handler}`);
            }
        });

        this.#eventListeners.delete(videoOutputId);
    }

    getTransactionTagValue() {
        const transactionIds = this.getTransactionIds();
        if (transactionIds.length) return transactionIds.length === 1 ? transactionIds[0] : `[${transactionIds.join(', ')}]`;
        else return 'noActiveTransaction';
    }

    getOutputCount() {
        return this.#videoOutputs.size;
    }

    hasVideoOutputs() {
        return this.#videoOutputs.size > 0;
    }

    clearAllVideoOutputs() {
        const outputIds = [...this.#videoOutputs.keys()];
        for (const outputId of outputIds) {
            this.removeVideoOutput(outputId);
        }
    }

    getCameraId() {
        return this.#cameraId;
    }
}
